import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { apolloClient } from './apollo';
import { identifyUser, resetAnalytics } from './posthog';

const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      firstName
      lastName
      role
      emailVerified
      hasCompletedOnboarding
      onboardingStep
      tourCompleted
      gettingStartedSteps
      avatarUrl
      avatarUpdatedAt
      notifySourceErrors
      notifyNewArticles
      notifyNewsletterReady
      organization {
        id
        name
        slug
        planType
        settings
        bannerUrl
        subscription {
          id
          status
          planType
          currentPeriodEnd
        }
      }
    }
  }
`;

const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      token
      user {
        id
        email
        firstName
        lastName
        role
      }
    }
  }
`;

const SIGNUP_MUTATION = gql`
  mutation Signup($input: SignupInput!) {
    signup(input: $input) {
      token
      user {
        id
        email
        firstName
        lastName
        role
      }
    }
  }
`;

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  emailVerified: boolean;
  hasCompletedOnboarding: boolean;
  onboardingStep: string;
  tourCompleted: boolean;
  gettingStartedSteps: Record<string, boolean> | null;
  avatarUrl: string | null;
  avatarUpdatedAt: string | null;
  // Notification preferences
  notifySourceErrors: boolean;
  notifyNewArticles: boolean;
  notifyNewsletterReady: boolean;
  organization: {
    id: string;
    name: string;
    slug: string;
    planType: string;
    settings: Record<string, unknown> | null;
    bannerUrl: string | null;
    subscription: {
      id: string;
      status: string;
      planType: string;
      currentPeriodEnd: string;
    } | null;
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: { email: string; password: string; firstName: string; lastName: string; organizationName: string }) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Start with loading=false if no token, otherwise true
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !!localStorage.getItem('token');
  });

  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token');

  const { data, error, loading: queryLoading, refetch } = useQuery(ME_QUERY, {
    skip: !hasToken,
  });

  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [signupMutation] = useMutation(SIGNUP_MUTATION);

  // Handle query completion
  useEffect(() => {
    if (!hasToken) {
      setLoading(false);
      return;
    }
    
    if (data?.me) {
      setUser(data.me);
      setLoading(false);
      identifyUser(data.me.id, {
        email: data.me.email,
        firstName: data.me.firstName,
        lastName: data.me.lastName,
        plan: data.me.organization?.planType,
        org: data.me.organization?.name,
        orgSlug: data.me.organization?.slug,
      });
    }
  }, [data, hasToken]);

  // Handle query errors
  useEffect(() => {
    if (error) {
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
    }
  }, [error]);

  // Sync loading state
  useEffect(() => {
    if (!queryLoading && hasToken && !data && !error) {
      // Query finished but no data/error yet - keep loading
    } else if (!queryLoading) {
      setLoading(false);
    }
  }, [queryLoading, hasToken, data, error]);

  const login = async (email: string, password: string) => {
    const { data } = await loginMutation({
      variables: { input: { email, password } },
    });
    localStorage.setItem('token', data.login.token);
    await apolloClient.resetStore();
    await refetch();
  };

  const signup = async (input: { email: string; password: string; firstName: string; lastName: string; organizationName: string }) => {
    const { data } = await signupMutation({
      variables: { input },
    });
    localStorage.setItem('token', data.signup.token);
    await apolloClient.resetStore();
    await refetch();
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    apolloClient.resetStore();
    resetAnalytics();
  };

  const refreshUser = async () => {
    const { data } = await refetch();
    if (data?.me) {
      setUser(data.me);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        refreshUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
